import React, { useState } from "react"

import { useForm } from "react-hook-form"

import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config.ts"
import { ButtonElement } from "@front-shared/elements/button.element.tsx"
import { InputElement } from "@front-shared/elements/input.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { TooltipElement } from "@front-shared/elements/tooltip.element.tsx"

import { TOOLTIPS_CONFIG } from "../../../config/tooltips.config"
import { CheckerHelper } from "../../../helpers/checker.helper"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { userDetailsSelector } from "../../../store/selectors/userDetails.selector"
import { checkerRestorePolicyThroughSmartScan } from "../../../store/thunks/checker.thunks"
import type { IDynamicCheckerFormInputData } from "../../../types/data.types.ts"

export const CoverageResultSmartScanComponent: React.FunctionComponent<
    ICoverageResultSmartScanComponentProps
> = props => {
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<Pick<IDynamicCheckerFormInputData, "zipCode"> | any>()

    const dispatch = useAppDispatch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const { selectedPracticeUsageQuota } = useAppSelector(userDetailsSelector)

    const [isSmartCheckRunning, setIsSmartCheckRunning] =
        useState<boolean>(false)

    const onRunSmartCoverageCheck = (
        data: Pick<IDynamicCheckerFormInputData, "zipCode">,
    ) => {
        if (!selectedPracticeUsageQuota) {
            return
        }

        CheckerHelper.runChecksWithQuotaConfirmation(
            selectedPracticeUsageQuota,
            () => {
                if (!selectedPracticeRole?.practice) {
                    return
                }

                setIsSmartCheckRunning(true)

                dispatch(
                    checkerRestorePolicyThroughSmartScan({
                        payload: {
                            currentRequestId: props.currentRequestId,
                            data: {
                                ...props.smartScanData,

                                zipCode: data.zipCode,
                            },
                        },
                        practice: selectedPracticeRole.practice,
                        onSuccess: props.onSmartScanSuccess,
                        onFinally: () => {
                            setIsSmartCheckRunning(false)
                        },
                    }),
                )
            },
            QuotaUsageType.SMART_SCAN,
            1,
        )
    }

    return (
        <div
            className={
                props.isFloating
                    ? "fixed bottom-[24px] horizontal-center z-10"
                    : `relative ${props.notFloatingClassName}`
            }
        >
            <div className="flex justify-center">
                <div
                    className={`
                        flex items-center justify-center gap-[8px]
                        rounded-full bg-brand-whitePurple border border-solid border-brand-lilac
                        py-[12px] pl-[32px]
                        ${isSmartCheckRunning ? "pr-[32px]" : "pr-[12px]"}
                        ${props.marginClassName}
                    `}
                >
                    <PrimaryText
                        className="relative top-[2px] flex-shrink-0"
                        weight="medium"
                    >
                        Patient ZIP
                    </PrimaryText>

                    <TooltipElement
                        text={TOOLTIPS_CONFIG.checker.zipCode}
                        placement="top"
                    />

                    <InputElement
                        className="w-[122px]"
                        placeholder="10000"
                        name="zipCode"
                        reactHookControl={control}
                        reactHookValidations={{
                            required: FRONT_VALIDATION_CONFIG.required,
                            pattern: FRONT_VALIDATION_CONFIG.zipShort,
                        }}
                        reactHookErrors={errors}
                        defaultValue={props.smartScanData.zipCode}
                        hideErrors
                        forceHighlightOnError
                    />

                    <ButtonElement
                        label="Recover policies with Nirvana Smart Scan™"
                        type="primary"
                        htmlType="button"
                        size="large"
                        className="w-[360px]"
                        onClick={handleSubmit(onRunSmartCoverageCheck)}
                        isLoading={isSmartCheckRunning}
                    />
                </div>
            </div>
        </div>
    )
}

interface ICoverageResultSmartScanComponentProps {
    notFloatingClassName?: string
    marginClassName?: string
    isFloating?: boolean
    currentRequestId: string
    smartScanData: IDynamicCheckerFormInputData
    onSmartScanSuccess?: (result: IOneVerifyCoverageCheckHistory) => void
}
